<template>
  <div id="forgot-mail-wrap">
    <form @submit.prevent="sendResetEmail">
      <span v-if="!isCountingDown">We will send a password reset email to <span v-html="modalMessage"></span>. Please follow the instructions in the email to reset your password.</span>
      <ModalMessage :message="feedbackMessage" :type="modalType" style="margin-bottom: 1rem;" />
      <button type="submit" class="fa-btn fa-btn-primary fa-btn-modal" :disabled="isCountingDown">Send Password Reset Email</button>
      <a href="javascript:void(0);" class="hint" style="margin-top: 1rem;" @click="changeToLogin">Back to Login</a>
    </form>
  </div>
</template>

  
<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useUIStore } from '~/store/uiStore';
import APIClient from '~/api/apiClient';
import { CSRFType } from '@/api/apiClient';
import CSRFManager from '~/utils/csrfManager.ts';
import { MessageType, ModalTypes } from '@/types/enums.ts';

const csrfManager = new CSRFManager(CSRFType.PASSWORD_FORGET);
const csrfToken = computed(() => csrfManager.state.csrfToken);
const modalMessage = computed(() => uiStore.modalMessage);
const feedbackMessage = ref('');
const modalType = ref(MessageType.INFO);

const apiClient = APIClient.getInstance();

const uiStore = useUIStore();

onMounted(async () => {
  await csrfManager.startFetchingToken();
});

onUnmounted(() => {
  csrfManager.stopFetchingToken();
});

let closeTimer = null;
let secondsLeft = ref(30);
const isCountingDown = ref(false);

function autoCloseModal() {
  if (closeTimer) {
    clearTimeout(closeTimer);
  }

  isCountingDown.value = true;

  const countdown = () => {
    if (secondsLeft.value > 0) {
      feedbackMessage.value = `Password reset mail sent to ${modalMessage.value}. This may take a few minutes to arrive. This window will close automatically in ${secondsLeft.value} seconds.`;
      secondsLeft.value -= 1;
    } else {
      uiStore.setModalType(ModalTypes.LOGIN);
      return;
    }

    closeTimer = setTimeout(countdown, 1000);
  };

  countdown();
}

function sendResetEmail() {
  // Regular expression for basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Check if modalMessage contains a valid email address
  if (!emailRegex.test(modalMessage.value)) {
    console.error('Invalid email address format.');
    feedbackMessage.value = 'Please enter a valid email address.';
    modalType.value = MessageType.ERROR;
    return; // Stop the function if the email is invalid
  }

  // Proceed if the email is valid
  apiClient.passwordForget(modalMessage.value, csrfToken.value).then((response) => {
    if (response.valid) {
      console.log('Password reset mail sent.');
      feedbackMessage.value = 'Password reset mail sent. Please check your inbox and follow the instructions to reset your password. This window will close automatically in 15 seconds.';
      modalType.value = MessageType.SUCCESS;
      autoCloseModal();
    } else {
      console.error('Error sending password reset mail:', response.message);
      feedbackMessage.value = response.error || 'An unexpected error occurred while sending the password reset email.';
      modalType.value = MessageType.ERROR;
    }
  });
}

function changeToLogin() {
  uiStore.setModalType(ModalTypes.LOGIN);
}
</script>
